import { PhoneCallRow } from '../../../types/supabase';

interface PhoneCallSummaryProps {
  phoneCall: PhoneCallRow;
  showDirection?: boolean;
  onClick?: () => void;
  className?: string;
}

export function PhoneCallSummary({
  phoneCall,
  showDirection = true,
  onClick,
  className = ""
}: PhoneCallSummaryProps) {
  const formatPhoneCallTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 24 * 7) {
      return date.toLocaleDateString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    }
  };

  const getStatusBadgeColor = () => {
    // Use analysis_outcome if available (takes precedence over status)
    if (phoneCall.analysis_outcome === 'success') {
      return 'bg-success';
    } else if (phoneCall.analysis_outcome === 'failure') {
      return 'bg-danger';
    }

    // Fall back to status
    switch (phoneCall.status) {
      case 'COMPLETED':
        return 'bg-success';
      case 'IN_PROGRESS':
      case 'RINGING':
        return 'bg-warning';
      case 'FAILED':
      case 'NO_ANSWER':
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  };

  const getStatusText = () => {
    // Use analysis_outcome if available
    if (phoneCall.analysis_outcome) {
      return phoneCall.analysis_outcome;
    }
    // Fall back to status
    return phoneCall.status?.toLowerCase().replace('_', ' ') || 'unknown';
  };

  const getSummaryText = () => {
    // Use analysis_summary if available, otherwise fall back to transcript
    if (phoneCall.analysis_summary) {
      return phoneCall.analysis_summary;
    }
    return phoneCall.transcript;
  };

  return (
    <div
      className={`message-summary ${className}`}
      onClick={onClick}
      style={onClick ? { cursor: 'pointer' } : {}}
    >
      <div className="d-flex align-items-center mb-1">
        {showDirection && (
          <div className="badge me-2 bg-success text-white">
            <i className="material-icons" style={{ fontSize: '14px', marginRight: '4px' }}>
              phone
            </i>
            <i className="material-icons" style={{ fontSize: '14px' }}>
              arrow_outward
            </i>
          </div>
        )}
        <div className={`badge me-2 ${getStatusBadgeColor()}`}>
          {getStatusText()}
        </div>
        <div className="small text-muted">
          {formatPhoneCallTime(phoneCall.created_at)}
        </div>
      </div>

      <div className="text-muted small">
        <div className="mb-1">
          <strong>AI Phone Call</strong>
        </div>
        {getSummaryText() && (
          <div style={{
            maxHeight: '2.4em',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical' as const
          }}>
            {getSummaryText()}
          </div>
        )}
      </div>
    </div>
  );
}
